import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { COMMISSION_LEVEL_OPTIONS, IMO, IMO_LABELS, selectableCommissionLevels, staffRoleLabel } from "@aam/shared";
import { getSessionToken, setSessionToken, setImpersonatorToken, useSession } from "../hooks/useSession";
import { useToast } from "../context/ToastContext";
import { Badge } from "./ui/Badge";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Label, Select } from "./ui/Input";
import { PageHeader } from "./ui/PageHeader";
import { CompensationTypeSelect } from "./CompensationTypeSelect";
import { DataTableSkeleton } from "./ui/DataTableSkeleton";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface UserListItem {
  id: string;
  portalUserId?: string | null;
  agentId?: string | null;
  email: string;
  organization: {
    id: string;
    name: string;
    brandName: string;
  } | null;
  role: "ADMIN" | "PPG" | "PROVIDER" | "AGENT";
  providerImo: string | null;
  isStaff: boolean;
  canLogin: boolean;
  firstName: string | null;
  lastName: string | null;
  accessScope: "OWN" | "DOWNLINE" | "ALL";
  canManageDownlines: boolean;
  commissionLevel: string | null;
  createdAt: string;
  updatedAt: string;
  upline: {
    id: string;
    displayName: string;
    email: string;
  } | null;
  _count: {
    sessions: number;
    modifiedContracts: number;
  };
}

type UserAudience = "all" | "staff" | "agents";

function displayName(item: UserListItem) {
  const name = [item.firstName, item.lastName].filter(Boolean).join(" ").trim();
  return name || null;
}

interface UserListResponse {
  items: UserListItem[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

const IMO_OPTIONS = Object.values(IMO);

interface DownlineCandidate {
  id: string;
  email: string;
  displayName: string | null;
}

interface OrganizationOption {
  id: string;
  slug: string;
  name: string;
  brandName: string;
  parentId: string | null;
}

export function UsersList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = useState<string | null>(null);
  const [data, setData] = useState<UserListResponse | null>(null);
  const [searchInput, setSearchInput] = useState(searchParams.get("search") ?? "");
  const [inviteRole, setInviteRole] = useState<"ADMIN" | "PROVIDER" | "AGENT">("AGENT");
  const [providerImo, setProviderImo] = useState<string>(IMO.PREMIER_PRODUCERS_GROUP);
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [inviteOrganizationId, setInviteOrganizationId] = useState("");
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);
  const [downlineQuery, setDownlineQuery] = useState("");
  const [downlineResults, setDownlineResults] = useState<DownlineCandidate[]>([]);
  const [downlineSearchLoading, setDownlineSearchLoading] = useState(false);
  const [selectedDownline, setSelectedDownline] = useState<DownlineCandidate | null>(null);
  const [downlineDropdownOpen, setDownlineDropdownOpen] = useState(false);
  const downlineSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [uplineCommissionLevel, setUplineCommissionLevel] = useState<string | null>(null);
  const [uplineCommissionLevelLoading, setUplineCommissionLevelLoading] = useState(false);
  const [inviteCommissionLevel, setInviteCommissionLevel] = useState("");
  const [inviteCompensationType, setInviteCompensationType] = useState("");
  const [permissionUpdatingId, setPermissionUpdatingId] = useState<string | null>(null);
  const token = useMemo(() => getSessionToken(), []);
  const { agent: currentUser } = useSession();
  const { success, error: toastError } = useToast();

  const organizationBrandName =
    organizations.find((organization) => organization.id === inviteOrganizationId)?.brandName ??
    null;
  // Altura is the root of the tree and is always a valid upline, so a brand new
  // organization can be seeded before it has any agents of its own.
  const rootOrganization = organizations.find((organization) => !organization.parentId) ?? null;
  const rootIsSelected = Boolean(rootOrganization && rootOrganization.id === inviteOrganizationId);

  const page = Math.max(1, Number(searchParams.get("page") || 1));
  const search = searchParams.get("search") ?? "";
  const audience = (searchParams.get("audience") ?? "all") as UserAudience;

  async function loadUsers() {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        page: String(page),
        pageSize: "25",
        audience,
      });
      if (search) query.set("search", search);
      const res = await fetch(`${API_BASE}/api/users?${query.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to load users");
      setData(json.data as UserListResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, search, audience]);

  useEffect(() => {
    if (inviteRole !== "AGENT" || !token || !downlineQuery.trim()) {
      setDownlineResults([]);
      return;
    }
    if (downlineSearchDebounce.current) clearTimeout(downlineSearchDebounce.current);
    downlineSearchDebounce.current = setTimeout(async () => {
      setDownlineSearchLoading(true);
      try {
        const query = new URLSearchParams({ search: downlineQuery.trim(), pageSize: "8" });
        if (inviteOrganizationId) query.set("organizationId", inviteOrganizationId);
        const res = await fetch(`${API_BASE}/api/agents?${query.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.error || "Failed to search agents");
        setDownlineResults(
          (json.data.items as Array<{ id: string; email: string; displayName: string | null }>).map(
            (item) => ({ id: item.id, email: item.email, displayName: item.displayName })
          )
        );
      } catch {
        setDownlineResults([]);
      } finally {
        setDownlineSearchLoading(false);
      }
    }, 300);
    return () => {
      if (downlineSearchDebounce.current) clearTimeout(downlineSearchDebounce.current);
    };
  }, [downlineQuery, inviteRole, inviteOrganizationId, token]);

  const selectableLevels = useMemo(
    () => selectableCommissionLevels(COMMISSION_LEVEL_OPTIONS.map((option) => option.value), uplineCommissionLevel),
    [uplineCommissionLevel]
  );

  useEffect(() => {
    if (!selectedDownline || !token) {
      setUplineCommissionLevel(null);
      setInviteCommissionLevel("");
      return;
    }
    let cancelled = false;
    setUplineCommissionLevelLoading(true);
    void fetch(`${API_BASE}/api/agents/${encodeURIComponent(selectedDownline.id)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        const json = await response.json();
        if (!response.ok || !json.success) throw new Error(json.error || "Unable to load agent");
        return json.data.commissionLevel as string | null;
      })
      .then((level) => {
        if (cancelled) return;
        setUplineCommissionLevel(level ?? null);
      })
      .catch(() => {
        if (!cancelled) setUplineCommissionLevel(null);
      })
      .finally(() => {
        if (!cancelled) setUplineCommissionLevelLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDownline, token]);

  useEffect(() => {
    if (selectableLevels.length === 0) {
      setInviteCommissionLevel("");
      return;
    }
    setInviteCommissionLevel((current) => (selectableLevels.includes(current) ? current : selectableLevels[0]));
  }, [selectableLevels]);

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);
      return copied;
    }
  }

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void fetch(`${API_BASE}/api/organizations`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        const json = await response.json();
        return response.ok && json.success ? (json.data as OrganizationOption[]) : [];
      })
      .catch(() => [])
      .then((rows) => {
        if (cancelled) return;
        setOrganizations(rows);
        setInviteOrganizationId((current) => {
          if (current && rows.some((row) => row.id === current)) return current;
          const own = currentUser?.organizationId;
          return own && rows.some((row) => row.id === own) ? own : rows[0]?.id ?? "";
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token, currentUser?.organizationId]);

  async function createInvite() {
    if (!token) return;
    if (inviteRole === "AGENT" && (!selectedDownline || !inviteCommissionLevel || !inviteCompensationType)) return;
    setInviting(true);
    setError(null);
    setInviteInfo(null);
    try {
      let inviteUrl: string;
      if (inviteRole === "AGENT") {
        const res = await fetch(`${API_BASE}/api/referral-codes`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ownerUserId: selectedDownline!.id,
            commissionLevel: inviteCommissionLevel,
            compensationType: inviteCompensationType,
          }),
        });
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.error || "Failed to create invite");
        inviteUrl = `${window.location.origin}/join/${json.data.code}`;
      } else {
        const body: { role: string; providerImo?: string; organizationId?: string } = {
          role: inviteRole,
        };
        if (inviteRole === "PROVIDER") {
          body.providerImo = providerImo;
        }
        if (inviteOrganizationId) {
          body.organizationId = inviteOrganizationId;
        }
        const res = await fetch(`${API_BASE}/api/user-invites`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.error || "Failed to create invite");
        inviteUrl = String(json.data.inviteUrl ?? "");
      }
      setInviteInfo(`Invite created: ${inviteUrl}`);
      if (inviteUrl && await copyToClipboard(inviteUrl)) {
        success("Copied");
      } else {
        toastError("Invite created, but the link could not be copied.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create invite");
    } finally {
      setInviting(false);
    }
  }

  async function toggleDownlinePermission(item: UserListItem) {
    if (!token) return;
    setPermissionUpdatingId(item.id);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/users/${item.id}/downline-permission`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ enabled: !item.canManageDownlines }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to update permission");
      setData((current) => current && {
        ...current,
        items: current.items.map((entry) =>
          entry.id === item.id ? { ...entry, canManageDownlines: !item.canManageDownlines } : entry
        ),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update permission");
    } finally {
      setPermissionUpdatingId(null);
    }
  }

  async function impersonateUser(userId: string) {
    if (!token) return;
    setImpersonatingId(userId);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/users/${userId}/impersonate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to impersonate user");
      setImpersonatorToken(token);
      setSessionToken(json.data.sessionToken as string);
      window.location.replace(String(json.data.redirectTo ?? "/dashboard/agents"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to impersonate user");
      setImpersonatingId(null);
    }
  }

  return (
    <section className="space-y-4">
      <PageHeader
        title="User management"
        description="Dashboard staff, provider logins, and registered agents with portal access. Session counts reflect active login history."
        actions={
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("page", "1");
                if (searchInput.trim()) next.set("search", searchInput.trim());
                else next.delete("search");
                return next;
              });
            }}
          >
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search user email"
              className="w-56"
            />
            <Button type="submit" variant="secondary" size="sm">
              Search
            </Button>
          </form>
        }
      />

      {error && (
        <div className="rounded-xl border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-border-default bg-surface-overlay p-4">
        <h2 className="text-sm font-semibold text-content-primary mb-1">Create invite link</h2>
        <div className="mb-3 inline-flex rounded-lg border border-border-default bg-surface-raised p-1" role="group" aria-label="Who are you inviting?">
          {([
            ["AGENT", "Invite an agent"],
            ["STAFF", "Invite staff"],
          ] as const).map(([kind, label]) => {
            const active = kind === "AGENT" ? inviteRole === "AGENT" : inviteRole !== "AGENT";
            return (
              <button
                key={kind}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  if (active) return;
                  setInviteRole(kind === "AGENT" ? "AGENT" : "ADMIN");
                  setSelectedDownline(null);
                  setDownlineQuery("");
                  setDownlineDropdownOpen(false);
                }}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  active ? "bg-accent-strong text-on-accent" : "text-content-secondary hover:text-content-primary"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        <p className="mb-4 text-sm text-content-secondary">
          {inviteRole === "AGENT"
            ? "Send a registration link that places a new agent under an upline, at a commission level, paid advance or as earned."
            : "Send a link to onboard a new staff member. They sign in and receive the selected access level for the chosen organization."}
        </p>
        <div className="flex flex-wrap items-end gap-3">
          {inviteRole !== "AGENT" && (
            <Label className="min-w-[140px]">
              Access level
              <Select
                className="mt-1.5"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as "ADMIN" | "PROVIDER")}
              >
                <option value="ADMIN">Admin</option>
                <option value="PROVIDER">Provider</option>
              </Select>
            </Label>
          )}
          {organizations.length > 0 && (
            <Label className="min-w-[200px]">
              Organization
              <Select
                className="mt-1.5"
                value={inviteOrganizationId}
                onChange={(e) => {
                  setInviteOrganizationId(e.target.value);
                  // An upline from the previous organization no longer applies.
                  setSelectedDownline(null);
                  setDownlineQuery("");
                  setDownlineDropdownOpen(false);
                }}
              >
                {organizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>
                    {organization.brandName}
                  </option>
                ))}
              </Select>
              <span className="mt-1 block text-xs text-content-muted">
                {inviteRole === "AGENT"
                  ? rootIsSelected || !rootOrganization
                    ? "Search an upline in this organization."
                    : `Search an upline in this organization or ${rootOrganization.brandName}.`
                  : "Everyone they invite inherits this organization."}
              </span>
            </Label>
          )}
          {inviteRole === "PROVIDER" && (
            <Label className="min-w-[180px]">
              IMO
              <Select
                className="mt-1.5"
                value={providerImo}
                onChange={(e) => setProviderImo(e.target.value)}
              >
                {IMO_OPTIONS.map((imo) => (
                  <option key={imo} value={imo}>{IMO_LABELS[imo]}</option>
                ))}
              </Select>
            </Label>
          )}
          {inviteRole === "AGENT" && (
            <Label className="relative min-w-[240px]">
              Upline agent
              {selectedDownline ? (
                <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border-default bg-surface-input px-4 py-2.5 text-sm text-content-primary">
                  <span className="flex-1 truncate">
                    {selectedDownline.displayName || selectedDownline.email}
                  </span>
                  <button
                    type="button"
                    className="text-xs font-semibold text-accent hover:opacity-90"
                    onClick={() => {
                      setSelectedDownline(null);
                      setDownlineQuery("");
                    }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <Input
                  className="mt-1.5"
                  value={downlineQuery}
                  placeholder="Search agent name or email…"
                  onChange={(e) => {
                    setDownlineQuery(e.target.value);
                    setDownlineDropdownOpen(true);
                  }}
                  onFocus={() => setDownlineDropdownOpen(true)}
                  onBlur={() => window.setTimeout(() => setDownlineDropdownOpen(false), 150)}
                  required
                />
              )}
              {downlineDropdownOpen && !selectedDownline && downlineQuery.trim() && (
                <div className="absolute z-10 mt-1 max-h-64 w-full min-w-[240px] overflow-auto rounded-xl border border-border-default bg-surface-raised shadow-card-hover">
                  {downlineSearchLoading ? (
                    <p className="px-4 py-2.5 text-sm text-content-muted">Searching…</p>
                  ) : downlineResults.length === 0 ? (
                    <p className="px-4 py-2.5 text-sm text-content-muted">
                      No agents found in {organizationBrandName ?? "this organization"}
                      {rootOrganization && !rootIsSelected ? ` or ${rootOrganization.brandName}` : ""}.
                    </p>
                  ) : (
                    downlineResults.map((candidate) => (
                      <button
                        key={candidate.id}
                        type="button"
                        className="block w-full px-4 py-2.5 text-left text-sm text-content-primary hover:bg-surface-overlay"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setSelectedDownline(candidate);
                          setDownlineDropdownOpen(false);
                        }}
                      >
                        <span className="block truncate font-medium">
                          {candidate.displayName || candidate.email}
                        </span>
                        {candidate.displayName && (
                          <span className="block truncate text-xs text-content-muted">{candidate.email}</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </Label>
          )}
          {inviteRole === "AGENT" && selectedDownline && (
            <Label className="min-w-[160px]">
              Commission level
              <Select
                className="mt-1.5"
                value={inviteCommissionLevel}
                disabled={uplineCommissionLevelLoading || selectableLevels.length === 0}
                onChange={(e) => setInviteCommissionLevel(e.target.value)}
              >
                {selectableLevels.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </Select>
              <span className="mt-1 block text-xs text-content-muted">
                {uplineCommissionLevelLoading
                  ? "Loading upline's level…"
                  : uplineCommissionLevel
                    ? `Capped at upline's own level (${uplineCommissionLevel})`
                    : "Upline has no level on file — any level is selectable"}
              </span>
            </Label>
          )}
          {inviteRole === "AGENT" && selectedDownline && (
            <CompensationTypeSelect
              className="min-w-[160px]"
              value={inviteCompensationType}
              onChange={setInviteCompensationType}
            />
          )}
          <Button
            onClick={createInvite}
            disabled={
              inviting ||
              (inviteRole === "AGENT" &&
                (!selectedDownline || !inviteCommissionLevel || !inviteCompensationType))
            }
            size="sm"
          >
            {inviting ? "Creating…" : "Create invite"}
          </Button>
        </div>
        {inviteInfo && (
          <p className="mt-3 text-xs text-status-active-text break-all">{inviteInfo}</p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {([
          ["all", "All accounts"],
          ["staff", "Staff & providers"],
          ["agents", "Registered agents"],
        ] as const).map(([value, label]) => (
          <Button
            key={value}
            size="sm"
            variant={audience === value ? "primary" : "secondary"}
            onClick={() => setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set("audience", value);
              next.set("page", "1");
              return next;
            })}
          >
            {label}
          </Button>
        ))}
      </div>

      {loading ? (
        <DataTableSkeleton
          headers={["Account", "Organization", "Access", "Upline", "Sessions", "Contracts modified", "Updated", "Actions"]}
          cellVariants={["multiline", "text", "badge", "text", "text", "text", "text", "short"]}
          loadingLabel="Loading user accounts"
        />
      ) : (
        <div className="overflow-auto rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-4 shadow-card">
          <table className="w-full min-w-[1140px] text-sm">
            <thead className="text-content-secondary text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left py-2 pr-3">Account</th>
                <th className="text-left py-2 pr-3">Organization</th>
                <th className="text-left py-2 pr-3">Access</th>
                <th className="text-left py-2 pr-3">Upline</th>
                <th className="text-left py-2 pr-3">Sessions</th>
                <th className="text-left py-2 pr-3">Contracts modified</th>
                <th className="text-left py-2 pr-3">Updated</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-content-muted">
                    No accounts match this filter.
                  </td>
                </tr>
              ) : (data?.items ?? []).map((item) => {
                const access = staffRoleLabel(item.role, item.providerImo);
                const name = displayName(item);
                const isRegisteredAgent = !item.isStaff && item.role === "AGENT";
                const managedAgentId = item.agentId ?? null;
                const canImpersonate =
                  item.id !== currentUser?.id &&
                  item.portalUserId !== currentUser?.portalUserId &&
                  (item.isStaff || item.role === "PROVIDER" || item.role === "PPG" || item.canLogin);
                return (
                  <tr key={item.id} className="border-t border-border-subtle align-top">
                    <td className="py-3 pr-3">
                      <div className="space-y-0.5">
                        {name && (
                          <p className="font-medium text-content-primary">{name}</p>
                        )}
                        {isRegisteredAgent && managedAgentId ? (
                          <Link
                            to={`/dashboard/agents/${managedAgentId}`}
                            className="text-sm text-accent hover:opacity-90"
                          >
                            {item.email}
                          </Link>
                        ) : (
                          <p className="text-sm text-content-primary">{item.email}</p>
                        )}
                        {isRegisteredAgent && !managedAgentId && (
                          <p className="text-xs text-content-muted">Portal account · no application yet</p>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-3">
                      {item.organization ? (
                        <span className="text-sm font-medium text-content-primary">
                          {item.organization.brandName}
                        </span>
                      ) : (
                        <span className="text-xs text-content-muted">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      <div className="space-y-0.5">
                        <Badge
                          variant={
                            item.role === "ADMIN"
                              ? "default"
                              : isRegisteredAgent
                                ? "outline"
                                : "accent"
                          }
                        >
                          {access.title}
                        </Badge>
                        {access.subtitle && (
                          <p className="text-xs text-content-muted">{access.subtitle}</p>
                        )}
                        {isRegisteredAgent && item.accessScope !== "OWN" && (
                          <p className="text-xs text-content-muted">
                            Scope: {item.accessScope.toLowerCase()}
                          </p>
                        )}
                        {isRegisteredAgent && item.commissionLevel && (
                          <p className="text-xs text-content-muted">
                            Commission: {item.commissionLevel}
                          </p>
                        )}
                        {isRegisteredAgent && (
                          <Button
                            type="button"
                            size="sm"
                            variant={item.canManageDownlines ? "primary" : "secondary"}
                            disabled={permissionUpdatingId === item.id}
                            onClick={() => void toggleDownlinePermission(item)}
                            className="mt-1"
                          >
                            {permissionUpdatingId === item.id
                              ? "Saving…"
                              : item.canManageDownlines
                                ? "Downlines: On"
                                : "Downlines: Off"}
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-3 max-w-[160px]">
                      {item.upline ? (
                        <Link
                          to={`/dashboard/agents/${item.upline.id}`}
                          className="block truncate text-xs text-content-primary hover:underline"
                          title={item.upline.email}
                        >
                          {item.upline.displayName}
                        </Link>
                      ) : (
                        <span className="text-xs text-content-muted">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-3 text-content-secondary" title="Total sessions recorded">
                      {item._count.sessions}
                    </td>
                    <td className="py-3 pr-3 text-content-secondary">{item._count.modifiedContracts}</td>
                    <td className="py-3 pr-3 text-content-secondary">{new Date(item.updatedAt).toLocaleString()}</td>
                    <td className="py-3 text-right">
                      {canImpersonate ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={impersonatingId === item.id}
                          onClick={() => void impersonateUser(item.id)}
                        >
                          {impersonatingId === item.id ? "Starting…" : "Impersonate"}
                        </Button>
                      ) : (
                        <span className="text-xs text-content-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex items-center justify-between mt-4 text-xs text-content-muted">
            <span>
              Page {data?.pagination.page ?? 1} / {data?.pagination.totalPages ?? 1}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={(data?.pagination.page ?? 1) <= 1}
                onClick={() => setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set("page", String(Math.max(1, (data?.pagination.page ?? 1) - 1)));
                  return next;
                })}
              >
                Prev
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={(data?.pagination.page ?? 1) >= (data?.pagination.totalPages ?? 1)}
                onClick={() => setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set("page", String((data?.pagination.page ?? 1) + 1));
                  return next;
                })}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
